import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useOpenGate } from '../../../../hooks/widget/dashboard/useOpenGate'

import Icon from '../../../ui/Icon'
import { Card } from '../../../card/Card'
import { Button } from '../../../buttons/Button'
import { GateSvg } from '../../../ui/gateSvg/GateSvg'

import { useApp } from '../../../../contexts/AppContext'
import { BannerKind } from '../../../../types/contexts/AppContext'

type GateAccessWidgetProps = {
  bookingStartTime?: string
}

const ACCESS_AVAILABLE_BEFORE_MS = 15 * 60 * 1000
const GATE_ACTION_COOLDOWN_SECONDS = 30
const GATE_OPEN_FEEDBACK_MS = 5 * 1000

export const GateAccessWidget = ({
  bookingStartTime,
}: GateAccessWidgetProps) => {
  const { t } = useTranslation('dashboard')
  const { showBanner } = useApp()
  const { openGate, loading, error: gateError } = useOpenGate()

  const [open, setOpen] = useState(false)
  const [cooldownSeconds, setCooldownSeconds] = useState(0)

  const bookingStart = bookingStartTime
    ? new Date(bookingStartTime).getTime()
    : Number.NaN

  const availableFrom = bookingStart - ACCESS_AVAILABLE_BEFORE_MS
  const available =
    Number.isFinite(availableFrom) && Date.now() >= availableFrom

  useEffect(() => {
    if (!available) {
      setOpen(false)
      setCooldownSeconds(0)
    }
  }, [available])

  useEffect(() => {
    if (cooldownSeconds <= 0) {
      return
    }

    const timeout = window.setTimeout(
      () => setCooldownSeconds(previous => Math.max(0, previous - 1)),
      1000
    )

    return () => window.clearTimeout(timeout)
  }, [cooldownSeconds])

  useEffect(() => {
    if (!open) {
      return
    }

    const timeout = window.setTimeout(() => {
      setOpen(false)
    }, GATE_OPEN_FEEDBACK_MS)

    return () => window.clearTimeout(timeout)
  }, [open])

  useEffect(() => {
    if (!gateError) {
      return
    }

    showBanner({
      kind: BannerKind.ERR,
      msg: gateError,
    })
  }, [gateError, showBanner])

  if (!bookingStartTime) {
    return null
  }

  const handleGateAction = async () => {
    if (!available || cooldownSeconds > 0 || loading) {
      return
    }

    const relayOpen = await openGate()

    if (relayOpen == null) {
      return
    }

    setOpen(relayOpen)
    setCooldownSeconds(GATE_ACTION_COOLDOWN_SECONDS)

    showBanner({
      kind: relayOpen ? BannerKind.OK : BannerKind.ERR,
      msg: t(
        relayOpen
          ? 'dashboard.gate-access.success.open'
          : 'dashboard.gate-access.error.not-opened'
      ),
    })
  }

  return (
    <Card className="col-12 col-xl-6">
      <div className="gate-access">
        <div className="gate-access-header">
          <div>
            <div className="eyebrow">{t('dashboard.gate-access.eyebrow')}</div>
            <h3>{t('dashboard.gate-access.title')}</h3>
          </div>
        </div>

        <div className="gate-access-illustration">
          <span
            className={`badge ${
              !available ? 'badge-warning' : open ? 'badge-accent' : ''
            }`}
          >
            {t(
              !available
                ? 'dashboard.gate-access.available-before'
                : open
                  ? 'dashboard.gate-access.status.command-sent'
                  : 'dashboard.gate-access.status.ready'
            )}
          </span>

          <GateSvg open={open} />
        </div>

        <p className="gate-access-description">
          {t(
            !available
              ? 'dashboard.gate-access.description.unavailable'
              : open
                ? 'dashboard.gate-access.description.command-sent'
                : 'dashboard.gate-access.description.ready'
          )}
        </p>

        <Button
          block
          variant="primary"
          icon={
            <Icon name={cooldownSeconds > 0 ? 'clock' : 'arrowR'} size={15} />
          }
          label={
            cooldownSeconds > 0
              ? t('dashboard.gate-access.action.retry-in', {
                  seconds: cooldownSeconds,
                })
              : t('dashboard.gate-access.action.open')
          }
          loading={loading}
          disabled={!available || cooldownSeconds > 0 || loading}
          onClick={handleGateAction}
        />
      </div>
    </Card>
  )
}
